/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FeedbackCommentProps,
  FeedbackSectionId,
  UserType,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { SuccessResponse } from '../../ReportsBeta/NewReport/api';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import FeedbackComment from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackComment';
import FeedbackCommentTextarea from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackCommentTextarea';
import React, { useCallback, useState } from 'react';
import { ApolloClient, FetchResult } from 'apollo-boost';
import styled from 'styled-components';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { ShowIndicatorFeedbackButton } from '../../NPOFeedback/MakeEdits/components/ImpactTable/ShowIndicatorFeedback';
import { isCommentNewSinceResolved } from './utils';

interface ImpactDetailCommentGroupProps {
  comments: FeedbackCommentProps[];
  title?: string;
  description?: string | JSX.Element;
  reportId: number;
  currentUserId?: string;
  currentUserName: string;
  currentUserEmail: string;
  currentUserType: UserType;
  hideDetails?: boolean;
  questionId: number;
  answer: Answer;
  details?: string;
  resolvedAt?: string | null;
  onSaveSuccess: (
    questionId: number,
    comment: FeedbackCommentProps,
  ) => unknown;
  handleDelete: (
    client: ApolloClient<object>,
    commentId: number,
    comment: string,
    reportId: number,
    sectionId: FeedbackSectionId,
  ) => Promise<
    FetchResult<
      SuccessResponse<'deleteComment'>,
      Record<string, any>,
      Record<string, any>
    >
  >;
  handleSave: (
    client: ApolloClient<object>,
    commentId: number,
    comment: string,
    reportId: number,
    sectionId: FeedbackSectionId,
    details?: string,
  ) => Promise<
    FetchResult<
      SuccessResponse<'updateComment'>,
      Record<string, any>,
      Record<string, any>
    >
  >;
  onSuccessSave: (comment: FeedbackCommentProps, index: number) => unknown;
  onSuccessDelete: (index: number) => unknown;
}

const StyledDiv = styled.div`
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;

  h3 {
    margin-top: 0;
    color: #333;
    font-size: 1.1rem;
    font-weight: 600;
  }

  .comment-group-description {
    color: #666;
    font-size: 0.9rem;
    margin-top: 0.5rem;
    margin-bottom: 1rem;
  }

  .add-comment-textarea {
    margin-top: ${SPACERS.medium};
  }
`;

const ImpactDetailCommentGroup: React.FC<ImpactDetailCommentGroupProps> = ({
  title,
  comments,
  reportId,
  description,
  currentUserId,
  currentUserName,
  currentUserEmail,
  hideDetails = true,
  currentUserType,
  questionId,
  answer,
  details,
  resolvedAt,
  handleDelete,
  handleSave,
  onSuccessSave,
  onSuccessDelete,
  onSaveSuccess,
}) => {
  const [open, setOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setOpen(o => !o);
  }, [setOpen]);

  if (comments.length === 0) return null;

  return (
    <StyledDiv>
      {title && (
        <TrueImpactText variant="heading-subheading">{title}</TrueImpactText>
      )}
      {description && (
        <p className="comment-group-description">{description}</p>
      )}
      <ShowIndicatorFeedbackButton
        show={open}
        reverseIcon={true}
        comments={comments}
        nullFeedbackText="Add Stage Feedback"
        toggleShow={handleToggle}
      />
      {open &&
        comments.map((comment, index) => (
          <FeedbackComment
            key={comment.commentId || index}
            index={index}
            comment={comment}
            reportId={reportId}
            isNew={isCommentNewSinceResolved(comment, resolvedAt)}
            hideModelButton={hideDetails}
            sectionId="indicator_details"
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            currentUserEmail={currentUserEmail}
            currentUserType={currentUserType}
            handleSave={handleSave}
            handleDelete={handleDelete}
            onSuccessSave={onSuccessSave}
            onSuccessDelete={onSuccessDelete}
          />
        ))}
      {open && currentUserType !== 'npo' && (
        <FeedbackCommentTextarea
          userType={currentUserType}
          currentUserId={currentUserId}
          currentUserEmail={currentUserEmail}
          userName={currentUserName}
          reportId={reportId}
          questionId={questionId}
          sectionId="indicator_details"
          answer={answer.text}
          details={details}
          hideModelButton={hideDetails}
          className="add-comment-textarea"
          onSaveSuccess={onSaveSuccess}
        />
      )}
    </StyledDiv>
  );
};

export default ImpactDetailCommentGroup;
